import type { CustomQueryOptions, Filter, MapCategory, MapData, Pagination } from "../types";
import { createBulkCategory, createCategory, deleteCategory, getCategoryMapData, getMapCategories, getMapCategory, getOrganisationMapData, updateCategory, updateMapData } from "./requests";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const useGetMapCategories = (workspaceId: string, filter?: Filter, options?: CustomQueryOptions<{data: MapCategory[], pagination: Pagination}>) => {
  return useQuery({
    queryKey: ['map-categories-all', `${workspaceId}-${JSON.stringify(filter)}`],
    queryFn: () => getMapCategories(workspaceId, filter),
    ...options
  });
};

export const useGetMapCategory = (categoryId: string, options?: CustomQueryOptions<MapCategory>) => {
  return useQuery({
    queryKey: ['map-category', categoryId],
    queryFn: () => getMapCategory(categoryId),
    ...options
  });
};

export const useCreateCategory = (onSuccess: (response: any) => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { workspace: string, name: string, tags: string[] }) => createCategory(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['map-categories-all'], exact: false});
    }
  });
};

export const useCreateBulkCategory = (onSuccess: (response: any) => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { workspace: string, categories: { name: string, tags: string[] }[] }) => createBulkCategory(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['map-categories-all'], exact: false});
    }
  });
};

export const useUpdateCategory = (categoryId: string, onSuccess: (response: any) => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string, tags: string[] }) => updateCategory(categoryId, data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['map-categories-all'], exact: false});
      queryClient.invalidateQueries({ queryKey: ['map-category', categoryId], exact: false});
    }
  });
};

export const useDeleteCategory = (onSuccess: (response: any) => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (categoryId: string) => deleteCategory(categoryId),
    onSuccess: (response) => {
      onSuccess(response);
      queryClient.invalidateQueries({ queryKey: ['map-categories-all'], exact: false});
    },
    onError: (error) => {
      onError(error);
    }
  });
};

export const useGetOrganisationMapData = (workspaceId: string, organisationId: string, options?: CustomQueryOptions<MapData[]>) => {
  return useQuery({
    queryKey: ['map-data', organisationId],
    queryFn: () => getOrganisationMapData(workspaceId, organisationId),
    ...options
  });
};

export const useUpdateMapData = (organisationId: string, onSuccess: (response: any) => void, onError: (error: any) => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update-map-data', organisationId],
    mutationFn: (data: {
      category: string;
      organisation: string;
      tags: string[];
    }[]) => updateMapData(data),
    onSuccess,
    onError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['map-data', organisationId] });
    }
  });
};

export const useGetCategoryMapData = (categoryId: string, options?: CustomQueryOptions<MapData[]>) => {
  return useQuery({
    queryKey: ['map-data-category', categoryId],
    queryFn: () => getCategoryMapData(categoryId),
    ...options
  });
};